/*
  When there is a payment performed, a client can be redirected to some payment providers.
  That means they quit our app, do some payment action, and go back to our app.
  We need to keep the state in local storage to not lose the data they provided (e.g. in the form inputs).
  The data from local storage should be accessible only from booking checkout pages or payment processed page.

  This component is made for removing the data from the local storage when it is not needed anymore.
*/

import React from "react";
import { useHistory } from "react-router";

import { ROUTES } from "@config/app.routing";

import { localStorageFacade } from "@services/LocalStorageFacade";

import { LOCAL_STORAGE_KEY as LOCAL_STORAGE_KEY_END_OF_TENANCY } from "@app/redux/endOfTenancy/endOfTenancy.typings";
import { LOCAL_STORAGE_KEY as LOCAL_STORAGE_KEY_BOOKING } from "@app/redux/booking/booking.typings";
import { LOCAL_STORAGE_KEY as LOCAL_STORAGE_KEY_B2B } from "@app/redux/b2b/b2b.typings";
import { LOCAL_STORAGE_KEY as LOCAL_STORAGE_KEY_LOCATIONS } from "@app/redux/locations/locations.typings";
import { LOCAL_STORAGE_KEY as LOCAL_STORAGE_KEY_SERVICE_LOCATION } from "@app/redux/serviceLocation/serviceLocation.typings";
import { LOCAL_STORAGE_KEY as LOCAL_STORAGE_KEY_GIFT_CARDS } from "@app/redux/giftCards/giftCards.typings";

const pathsToKeepLocalStorage = [
  ROUTES.BOOKING_CHECKOUT,
  ROUTES.END_OF_TENANCY_CHECKOUT,
  ROUTES.B2B_CHECKOUT,
  ROUTES.PAYMENT_PROCESSED,
  `${ROUTES.GIFT_CARDS}${ROUTES.GIFT_CARDS_BUY}`,
];

const localStorageKeysToClean = [
  LOCAL_STORAGE_KEY_BOOKING,
  LOCAL_STORAGE_KEY_END_OF_TENANCY,
  LOCAL_STORAGE_KEY_B2B,
  LOCAL_STORAGE_KEY_LOCATIONS,
  LOCAL_STORAGE_KEY_SERVICE_LOCATION,
  LOCAL_STORAGE_KEY_GIFT_CARDS,
];

export const LocalStoragePurger = (): null => {
  const history = useHistory();
  const localStorage = localStorageFacade();

  React.useEffect(() => {
    const listener = history.listen(location => {
      if (pathsToKeepLocalStorage.includes(location.pathname)) return;

      localStorageKeysToClean.forEach(key => {
        localStorage.removeItem(key);
      });
    });

    return listener;
  }, []);

  return null;
};
